/* eslint-disable no-continue */

/**
 * NOTE: We are intentionally ignoring redirects that lead to other websites in this file.
 * I'd rather them be taken care of by the server than client, so we don't have to worry
 * about leaking any url hash or search query or something as a result of an oversight here.
 */

import { compile, type Key, pathToRegexp } from 'path-to-regexp'

import logger from '@/utils/logger'
import { REDIRECTS as UNSAFE_REDIRECTS } from '@/utils/redirects'

import { WEBSITE_SETTINGS } from '@/client/settings'

import { WEBSITE_URL } from './constants'

interface Redirect {
  source: string
  destination: string
  permanent: boolean
  has?: { type: string; key: string; value: string }[]
}
interface CompiledRedirect {
  source: RegExp
  destination: (args: Record<string, string>) => string
  has: Redirect['has']
  pathParams: (string | number)[]
}

// NOTE: Intentionally not type-asserting this (with 'as Redirect[]')
// because we want Typescript (it already infers the type of the JS import)
// to validate the shape of the imported object.
const REDIRECTS: Redirect[] = UNSAFE_REDIRECTS

let compiledRedirects: CompiledRedirect[] | null = null
function getCompiledRedirects() {
  if (compiledRedirects) {
    return compiledRedirects
  }

  compiledRedirects = REDIRECTS.map(({ source, destination, has }) => {
    let hostName = ''
    let destinationToUse = destination

    if (!destination.startsWith('/')) {
      const url = new URL(destination)
      if (destination.startsWith(url.origin)) {
        hostName = url.origin
        destinationToUse = destination.slice(url.origin.length)
      }
    }

    const pathParams: Key[] = []
    const sourceRegexp = pathToRegexp(source, pathParams)

    const destinationRegexp = compile<Record<string, string>>(destinationToUse, { validate: false })

    return {
      source: sourceRegexp,
      destination: (params: Record<string, string>) => `${hostName}${destinationRegexp(params)}`,
      has,
      pathParams: pathParams.map(item => item.name),
    }
  })

  return compiledRedirects
}

export function getRedirect(trimmedHref: string): string | null {
  if (
    trimmedHref.startsWith(WEBSITE_URL) ||
    (WEBSITE_SETTINGS.websiteName != null && trimmedHref.startsWith(WEBSITE_SETTINGS.websiteName))
  ) {
    logger.warning('getRedirect: trimmedHref should not start with WEBSITE_URL or WEBSITE_NAME', {
      trimmedHref,
    })
    return null
  }

  const redirects = getCompiledRedirects()
  let fullUrl: URL

  try {
    fullUrl = new URL(trimmedHref, WEBSITE_URL)
  } catch (err) {
    logger.error('getRedirect: Failed to parse URL', { trimmedHref, err })
    return null
  }

  for (const compiledRedirect of redirects) {
    const match = compiledRedirect.source.exec(fullUrl.pathname)
    if (!match) {
      continue
    }
    if (
      compiledRedirect.has != null &&
      !compiledRedirect.has.every(param => fullUrl.searchParams.get(param.key) === param.value)
    ) {
      continue
    }

    const params = Object.fromEntries(
      compiledRedirect.pathParams.map((item, index) => [item, match[index + 1]])
    )
    let destination = compiledRedirect.destination(params)

    if (fullUrl.search) {
      destination += destination.includes('?') ? `&${fullUrl.search.slice(1)}` : fullUrl.search
    }

    return destination
  }

  return null
}
